import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Search } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { DataPagination } from '@/components/DataPagination';

/* ==================================================================
 * Brand Activity — cross-brand team audit log (admin oversight)
 * ==================================================================
 * Full record of every brand team action (all roles, all brands),
 * from the brand_audit_logs table in the Creator App DB (read via the
 * admin-proxy). The brand-side Activity page hides admin-done actions;
 * this admin view shows everything.
 * ================================================================== */

type AuditRow = {
  id: string;
  brand_id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  summary: string | null;
  metadata: Record<string, any> | null;
  created_at: string;
};

const ROLE_STYLES: Record<string, string> = {
  admin: 'bg-pink-500/15 text-pink-600 border-pink-500/30',
  editor: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  viewer: 'bg-muted text-muted-foreground border-border',
  system: 'bg-green-500/15 text-green-600 border-green-500/30',
};

const PAGE_SIZE_DEFAULT = 25;

function prettyAction(action: string): string {
  return action.split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' · ');
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function BrandActivity() {
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [impFilter, setImpFilter] = useState<string>('all'); // all | impersonated | direct
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  // Brand names for the filter dropdown + row labels.
  const { data: brands = [] } = useQuery({
    queryKey: ['brand-activity-brands'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_configs')
        .select('id, brand_name')
        .order('brand_name', { ascending: true })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as { id: string; brand_name: string }[];
    },
  });
  const brandNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of brands) m.set(b.id, b.brand_name);
    return m;
  }, [brands]);

  // Latest logs (optionally scoped to one brand). Client-side search/role filter
  // + pagination on top (the admin-proxy has no offset/range support).
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['brand-activity', brandFilter],
    queryFn: async () => {
      let q = supabase
        .from('brand_audit_logs')
        .select('id, brand_id, actor_user_id, actor_email, actor_role, action, entity_type, entity_id, summary, metadata, created_at')
        .order('created_at', { ascending: false })
        .limit(500);
      if (brandFilter !== 'all') q = q.eq('brand_id', brandFilter);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as AuditRow[];
    },
  });

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return logs.filter(l => {
      if (roleFilter !== 'all' && (l.actor_role ?? '') !== roleFilter) return false;
      const impersonated = !!(l.metadata && l.metadata.via_admin_impersonation);
      if (impFilter === 'impersonated' && !impersonated) return false;
      if (impFilter === 'direct' && impersonated) return false;
      if (!s) return true;
      const brandName = brandNameById.get(l.brand_id) ?? '';
      const impEmail = l.metadata?.impersonator?.email ?? '';
      return (
        (l.actor_email ?? '').toLowerCase().includes(s) ||
        l.action.toLowerCase().includes(s) ||
        (l.summary ?? '').toLowerCase().includes(s) ||
        brandName.toLowerCase().includes(s) ||
        impEmail.toLowerCase().includes(s)
      );
    });
  }, [logs, roleFilter, impFilter, search, brandNameById]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Reset to page 1 whenever filters change the result set size.
  const resetPage = () => setPage(1);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Activity className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Brand Activity</h1>
          <p className="text-sm text-muted-foreground">
            Every team action across all brands — who did what, and with which role.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="gap-4">
          <CardTitle className="text-base">Activity log</CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search email, action, summary, brand…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); resetPage(); }}
                className="pl-9"
              />
            </div>
            <Select value={brandFilter} onValueChange={(v) => { setBrandFilter(v); resetPage(); }}>
              <SelectTrigger className="w-[220px]"><SelectValue placeholder="All brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.brand_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); resetPage(); }}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="All roles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="editor">Editor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
            <Select value={impFilter} onValueChange={(v) => { setImpFilter(v); resetPage(); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="impersonated">Impersonated only</SelectItem>
                <SelectItem value="direct">Direct only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-16 text-center text-sm text-muted-foreground">Loading activity…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">No activity matches your filters.</div>
          ) : (
            <>
              <div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Summary</TableHead>
                      <TableHead className="text-right">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageRows.map(l => (
                      <TableRow key={l.id}>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground" title={new Date(l.created_at).toISOString()}>
                          {fmtWhen(l.created_at)}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {brandNameById.get(l.brand_id) ?? <span className="text-muted-foreground">{l.brand_id.slice(0, 8)}</span>}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="truncate max-w-[200px]">{l.actor_email ?? 'system'}</span>
                            <Badge variant="outline" className={ROLE_STYLES[l.actor_role ?? ''] ?? 'text-muted-foreground'}>
                              {l.actor_role ?? 'unknown'}
                            </Badge>
                            {l.metadata?.via_admin_impersonation && (
                              <Badge
                                variant="outline"
                                className="bg-amber-500/15 text-amber-600 border-amber-500/30"
                                title={l.metadata?.impersonator?.email ? `Impersonated by ${l.metadata.impersonator.email}` : 'Performed via admin impersonation'}
                              >
                                Impersonated
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">{prettyAction(l.action)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[320px] truncate" title={l.summary ?? ''}>
                          {l.summary ?? '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => setSelected(l)}>View</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DataPagination
                currentPage={page}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={filtered.length}
                onPageChange={setPage}
                onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
                className="mt-4"
              />
            </>
          )}
        </CardContent>
      </Card>

      <DetailsDialog log={selected} brandName={selected ? brandNameById.get(selected.brand_id) : undefined} onClose={() => setSelected(null)} />
    </div>
  );
}

function DetailsDialog({ log, brandName, onClose }: { log: AuditRow | null; brandName?: string; onClose: () => void }) {
  if (!log) return null;
  const meta = (log.metadata ?? {}) as Record<string, any>;
  const changes = (meta.changes && typeof meta.changes === 'object' ? meta.changes : null) as Record<string, { from: any; to: any }> | null;
  const fmt = (v: any) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

  return (
    <Dialog open={!!log} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {prettyAction(log.action)}
            <Badge variant="outline" className={ROLE_STYLES[log.actor_role ?? ''] ?? 'text-muted-foreground'}>{log.actor_role ?? 'unknown'}</Badge>
          </DialogTitle>
        </DialogHeader>
        {log.summary && <p className="text-sm text-muted-foreground">{log.summary}</p>}

        <dl className="grid grid-cols-[110px_1fr] gap-x-4 gap-y-2 text-sm mt-2">
          <dt className="text-muted-foreground">Brand</dt><dd>{brandName ?? log.brand_id}</dd>
          <dt className="text-muted-foreground">Performed by</dt><dd>{log.actor_email ?? 'system'}</dd>
          <dt className="text-muted-foreground">Role</dt><dd>{log.actor_role ?? 'unknown'}</dd>
          <dt className="text-muted-foreground">When</dt><dd>{new Date(log.created_at).toLocaleString()}</dd>
          <dt className="text-muted-foreground">Entity</dt>
          <dd>{log.entity_type ? `${log.entity_type}${log.entity_id ? ` · ${log.entity_id.slice(0, 8)}` : ''}` : '—'}</dd>
          {log.metadata?.via_admin_impersonation && (
            <>
              <dt className="text-muted-foreground">Impersonated by</dt>
              <dd className="text-amber-600">{log.metadata?.impersonator?.email ?? 'BMC admin'}</dd>
            </>
          )}
        </dl>

        {changes && Object.keys(changes).length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Changes</div>
            <div className="space-y-2">
              {Object.entries(changes).map(([field, ch]) => (
                <div key={field} className="flex items-center gap-2 text-sm bg-muted/40 rounded-md px-3 py-2">
                  <span className="font-medium min-w-[120px]">{field.replace(/_/g, ' ')}</span>
                  <span className="line-through text-red-500/80">{fmt(ch?.from)}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="text-green-600">{fmt(ch?.to)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <details className="mt-4">
          <summary className="text-xs text-muted-foreground cursor-pointer">Raw JSON</summary>
          <pre className="mt-2 p-3 rounded-md bg-muted text-xs overflow-x-auto">{JSON.stringify(log, null, 2)}</pre>
        </details>
      </DialogContent>
    </Dialog>
  );
}
